import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bell, Gift, CalendarCheck, GraduationCap, Wallet, Check } from "lucide-react";

const ICONS = { referral: Gift, session: CalendarCheck, scholarship: GraduationCap, payout: Wallet };

export default function NotificationBell({ testId = "notification-bell", align = "right" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setUnread(0);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid={testId} onClick={toggle}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-white/8 bg-white/[0.03] text-slate-300 transition-all hover:text-white hover:bg-white/[0.06]">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notification-unread-badge"
            className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown"
          className={`absolute z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-white/10 bg-[#131C2E] shadow-2xl overflow-hidden ${align === "left" ? "left-0" : "right-0"}`}>
          <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
            <p className="font-display text-sm font-semibold text-white">Notifications</p>
            <span className="text-xs text-slate-500">{items.length}</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">
                <Bell className="mx-auto mb-2 h-6 w-6 text-slate-600" />
                No notifications yet.
              </div>
            ) : (
              items.map((n) => {
                const Icon = ICONS[n.type] || Check;
                return (
                  <div key={n.id} data-testid="notification-item"
                    className={`flex gap-3 border-b border-white/5 px-4 py-3 ${n.read ? "" : "bg-cyan-400/[0.04]"}`}>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cyan-400/10 text-cyan-300">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{n.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{n.body}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
